import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Manager - Scientific day/night switching
const useThemeManager = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const updateTheme = () => {
      const hour = new Date().getHours();
      // Scientific research: Light therapy affects circadian rhythm
      // Dark mode: 18:00-06:00 (better for evening melatonin production)
      // Light mode: 06:00-18:00 (supports daytime alertness)
      setIsDarkMode(hour >= 18 || hour < 6);
    };

    updateTheme();
    const interval = setInterval(updateTheme, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  return { isDarkMode };
};

// Matrix Pill Selector Component
const MatrixPillSelector = ({ onSelect, currentMode }) => {
  return (
    <div className="matrix-selector">
      <div className="matrix-quote">
        <p className="text-lg mb-6 text-gray-300 font-mono">
          "Välj din verklighet..."
        </p>
      </div>
      <div className="pills-container flex justify-center space-x-8">
        <button
          onClick={() => onSelect('text')}
          className={`pill blue-pill ${currentMode === 'text' ? 'selected' : ''}`}
          title="Textbaserad AI-assistent"
        >
          <div className="pill-inner">
            <span className="pill-icon">💬</span>
            <span className="pill-text">Text AI</span>
          </div>
        </button>
        <button
          onClick={() => onSelect('video')}
          className={`pill red-pill ${currentMode === 'video' ? 'selected' : ''}`}
          title="Video AI-assistent med röst"
        >
          <div className="pill-inner">
            <span className="pill-icon">🎥</span>
            <span className="pill-text">Video AI</span>
          </div>
        </button>
      </div>
    </div>
  );
};

// AI Avatar Component (Owner's Animated Avatar)
const AIAvatar = ({ isActive, isDarkMode, isAnimating }) => {
  const [currentAnimation, setCurrentAnimation] = useState('idle');
  
  useEffect(() => {
    if (isAnimating) {
      const animations = ['talking', 'thinking', 'explaining'];
      const randomAnimation = animations[Math.floor(Math.random() * animations.length)];
      setCurrentAnimation(randomAnimation);
      
      const timer = setTimeout(() => {
        setCurrentAnimation('idle');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  return (
    <div className={`ai-avatar-container ${isActive ? 'active' : ''} ${currentAnimation}`}>
      <div className="avatar-wrapper">
        <div className="avatar-glow"></div>
        <div className="avatar-image-container">
          <img 
            src="/new-ai-avatar.jpg"
            alt="SD Elektriska CEO AI Avatar" 
            className={`avatar-image ${currentAnimation}`}
          />
          <div className="avatar-expressions">
            <div className="eye-blink left-eye"></div>
            <div className="eye-blink right-eye"></div>
            <div className="mouth-animation"></div>
          </div>
        </div>
        <div className="ai-pulse"></div>
        
        {/* Speaking Animation */}
        {currentAnimation === 'talking' && (
          <div className="speech-waves">
            <div className="wave-1"></div>
            <div className="wave-2"></div>
            <div className="wave-3"></div>
          </div>
        )}
      </div>
      
      <div className="avatar-status">
        <div className={`status-indicator ${isActive ? 'online' : 'offline'}`}></div>
        <span className="status-text">
          {isActive ? 'CEO AI Online' : 'CEO AI Ready'}
        </span>
      </div>
      
      <div className="avatar-intro">
        <p className="ceo-name">AI Elektriker</p>
        <p className="ceo-tagline">Din personliga AI-elektriker</p>
      </div>
    </div>
  );
};

// Voice Commands Interface
const VoiceCommandInterface = ({ onCommand, isListening, startListening, stopListening }) => {
  return (
    <div className="voice-command-interface">
      <button
        onClick={isListening ? stopListening : startListening}
        className={`voice-btn ${isListening ? 'listening' : ''}`}
        title="Säg 'Sdelai kalkylator' eller 'Boka möte'"
      >
        <div className="mic-icon">
          {isListening ? '🔴' : '🎤'}
        </div>
        <div className="voice-waves">
          <div className="wave"></div>
          <div className="wave"></div>
          <div className="wave"></div>
        </div>
      </button>
      <p className="voice-hint">
        Säg: "Sdelai kalkylator" eller "Boka möte"
      </p>
    </div>
  );
};

// Enhanced AI Assistant Component with real CEO avatar
const AIAssistant = ({ mode, isDarkMode }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [language, setLanguage] = useState('sv');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [avatarAnimating, setAvatarAnimating] = useState(false);
  
  // Ref для автоматической прокрутки чата
  const chatMessagesRef = useRef(null);
  
  // Автоматическая прокрутка вниз при новых сообщениях
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, loading]); // Срабатывает при изменении сообщений или loading

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;
    
    const userMessage = { message: messageText, language };
    setMessages(prev => [...prev, { ...userMessage, type: 'user' }]);
    setInputMessage('');
    setLoading(true);
    setAvatarAnimating(true);

    try {
      const response = await axios.post(`${API}/ai-assistant`, userMessage);
      setMessages(prev => [...prev, { 
        message: response.data.response, 
        type: 'ai',
        language: response.data.language 
      }]);

      // Check for booking intent
      if (messageText.toLowerCase().includes('boka') || 
          messageText.toLowerCase().includes('möte') ||
          messageText.toLowerCase().includes('tid')) {
        handleBookingIntent(response.data.response);
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [...prev, { 
        message: 'Tyvärr, jag är inte tillgänglig just nu. Kontakta oss på 0175 100 70.', 
        type: 'ai' 
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => setAvatarAnimating(false), 2000);
    }
  };

  const handleBookingIntent = (aiResponse) => {
    // Integration with WhatsApp/Telegram/Email
    const bookingData = {
      timestamp: new Date().toISOString(),
      conversation: messages,
      aiResponse: aiResponse
    };
    
    // Send to WhatsApp
    const whatsappMessage = `🤖 Ny bokningsförfrågan från sdelai.se:\n\n${aiResponse}\n\n📅 Tidsstämpel: ${new Date().toLocaleString('sv-SE')}`;
    const whatsappUrl = `https://wa.me/46175100070?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const startVoiceListening = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.lang = language === 'sv' ? 'sv-SE' : language === 'en' ? 'en-US' : 'ru-RU';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        sendMessage(transcript);
      };

      recognition.start();
    }
  };

  const stopVoiceListening = () => {
    setIsListening(false);
  };

  if (mode === 'video') {
    return (
      <div className={`video-assistant ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="video-header">
          <AIAvatar isActive={true} isDarkMode={isDarkMode} isAnimating={avatarAnimating} />
          <div className="video-controls">
            <select 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
              className="language-selector"
            >
              <option value="sv">🇸🇪 Svenska</option>
              <option value="en">🇬🇧 English</option>
              <option value="ru">🇷🇺 Русский</option>
            </select>
          </div>
        </div>

        <div className="video-chat-area">
          <div className="messages-container" ref={chatMessagesRef}>
            {messages.length === 0 && (
              <div className="ceo-welcome">
                <div className="welcome-bubble">
                  <h4>👋 Hej! Jag är AI Elektriker</h4>
                  <p>Professionell AI-assistent för SD Elektriska AB.</p>
                  <p>Fråga mig om priser, boka tid eller få expertråd!</p>
                </div>
              </div>
            )}
            
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.type}`}>
                <div className="message-content">
                  {msg.type === 'ai' && (
                    <div style={{position: 'relative', display: 'inline-block'}}>
                      <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="message-avatar" />
                      <div className="ai-status-indicator"></div>
                    </div>
                  )}
                  <div className="message-text">{msg.message}</div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="message ai">
                <div className="message-content loading">
                  <div style={{position: 'relative', display: 'inline-block'}}>
                    <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="message-avatar" />
                    <div className="ai-status-indicator"></div>
                  </div>
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="video-input-area">
            <VoiceCommandInterface 
              onCommand={sendMessage}
              isListening={isListening}
              startListening={startVoiceListening}
              stopListening={stopVoiceListening}
            />
            <div className="text-input-group">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Prata med AI Assistent..."
                className="video-text-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading}
                className="send-btn"
              >
                📤
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Text mode with CEO context
  return (
    <div className={`text-assistant ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="chat-header">
        <div className="header-avatar">
          <div style={{position: 'relative', display: 'inline-block'}}>
            <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="mini-avatar" />
            <div className="ai-status-indicator"></div>
          </div>
          <div>
            <h3 className="assistant-title">AI Elektriker</h3>
            <p className="assistant-subtitle">SD Elektriska AB</p>
          </div>
        </div>
        <select 
          value={language} 
          onChange={(e) => setLanguage(e.target.value)}
          className="language-selector"
        >
          <option value="sv">🇸🇪 Svenska</option>
          <option value="en">🇬🇧 English</option>
          <option value="ru">🇷🇺 Русский</option>
        </select>
      </div>
      
      <div className="chat-messages" ref={chatMessagesRef}>
        {messages.length === 0 && (
          <div className="welcome-message">
            <div className="ceo-intro">
              <div style={{position: 'relative', display: 'inline-block'}}>
                <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="intro-avatar" />
                <div className="ai-status-indicator"></div>
              </div>
              <div>
                <h4>👋 Hej! Jag är AI Elektriker</h4>
                <p>Professionell AI-assistent för SD Elektriska AB. Jag hjälper dig med alla elfrågor!</p>
                <div className="quick-questions">
                  <button onClick={() => sendMessage("Vad kostar en laddbox?")} className="quick-btn">
                    Laddbox pris? 🔌
                  </button>
                  <button onClick={() => sendMessage("Boka tid för elcentral byte")} className="quick-btn">
                    Boka tid 📅
                  </button>
                  <button onClick={() => sendMessage("ROT avdrag förklaring")} className="quick-btn">
                    ROT avdrag? 💰
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.type}`}>
            <div className="message-content">
              {msg.type === 'ai' && (
                <div style={{position: 'relative', display: 'inline-block'}}>
                  <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="message-avatar" />
                  <div className="ai-status-indicator"></div>
                </div>
              )}
              <div className="message-text">{msg.message}</div>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="message ai">
            <div className="message-content loading">
              <div style={{position: 'relative', display: 'inline-block'}}>
                <img src="/new-ai-avatar.jpg" alt="AI Elektriker" className="message-avatar" />
                <div className="ai-status-indicator"></div>
              </div>
              <div className="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="chat-input">
        <VoiceCommandInterface 
          onCommand={sendMessage}
          isListening={isListening}
          startListening={startVoiceListening}
          stopListening={stopVoiceListening}
        />
        <div className="input-group">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Fråga AI Assistent..."
            className="chat-input-field"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading}
            className="send-button"
          >
            Skicka
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Price Calculator
const PriceCalculator = ({ isDarkMode }) => {
  const [formData, setFormData] = useState({
    service_type: '',
    description: '',
    house_size: '',
    distance_meters: '',
    complexity_level: 'standard',
    is_emergency: false,
    additional_info: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const requestData = {
        ...formData,
        distance_meters: formData.distance_meters ? parseInt(formData.distance_meters) : null
      };
      const response = await axios.post(`${API}/calculate-price`, requestData);
      setResult(response.data);
    } catch (error) {
      console.error('Price calculation error:', error);
      alert('Kunde inte beräkna priset. Kontakta oss direkt.');
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = [
    { value: 'electrical_panel_replacement', label: 'Byte av elcentral (3-fas, 10-16 automater)' },
    { value: 'car_charger_installation', label: 'Installation laddbox bil (standard)' },
    { value: 'car_charger_with_balancer', label: 'Installation laddbox + lastbalanserare' },
    { value: 'solar_panel_installation', label: 'Installation solpaneler komplett system (11.16 kW)' },
    { value: 'house_rewiring_130m2', label: 'Omledning hus (~130m²)' },
    { value: 'lighting_frd_installation', label: 'Belysning FRD + uttag fasad' },
    { value: 'plejd_smart_system', label: 'Plejd smart system installation' },
    { value: 'power_upgrade_20_25a', label: 'Ökning effekt 20A→25A' },
    { value: 'kitchen_wiring', label: 'Kök: ugn, uttag, belysning' },
    { value: 'cat6_camera_installation', label: 'Cat6 kamerasystem (flera kablar)' },
    { value: 'complex_renovation', label: 'Komplex renovering (kök+rum)' },
    { value: 'meter_pole_installation', label: 'Mätarstolpe + grävning' },
    { value: 'outlet_installation', label: 'Installation av uttag' },
    { value: 'stove_connection', label: 'Anslutning spis/häll' },
    { value: 'washing_machine_connection', label: 'Anslutning tvättmaskin' },
    { value: 'air_conditioning_connection', label: 'Anslutning luftkonditionering' },
    { value: 'electrical_inspection', label: 'Elbesiktning' },
    { value: 'emergency_repair', label: 'Akut reparation' }
  ];

  return (
    <div className={`price-calculator ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="calculator-header">
        <h3 className="calculator-title">🤖 AI-Prisberäkning</h3>
        <p className="calculator-subtitle">Baserad på verkliga projekt med inflationskorrigering</p>
      </div>
      
      <form onSubmit={handleSubmit} className="calculator-form">
        <div className="form-group">
          <label className="form-label">Typ av tjänst *</label>
          <select
            value={formData.service_type}
            onChange={(e) => setFormData({...formData, service_type: e.target.value})}
            required
            className="form-select"
          >
            <option value="">Välj tjänst</option>
            {serviceOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Beskrivning av arbetet *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
            rows="3"
            className="form-textarea"
            placeholder="Beskriv vad som behöver göras..."
          />
        </div>
        
        {formData.service_type === 'house_rewiring_130m2' && (
          <div className="form-group">
            <label className="form-label">Husstorlek (m²)</label>
            <input
              type="text"
              value={formData.house_size}
              onChange={(e) => setFormData({...formData, house_size: e.target.value})}
              className="form-input"
              placeholder="t.ex. 130 m²"
            />
          </div>
        )}
        
        {formData.service_type === 'air_conditioning_connection' && (
          <div className="form-group">
            <label className="form-label">Avstånd från elcentral (meter)</label>
            <input
              type="number"
              value={formData.distance_meters}
              onChange={(e) => setFormData({...formData, distance_meters: e.target.value})}
              className="form-input"
              placeholder="t.ex. 10"
            />
          </div>
        )}
        
        <div className="form-group">
          <label className="form-label">Komplexitet</label>
          <select
            value={formData.complexity_level}
            onChange={(e) => setFormData({...formData, complexity_level: e.target.value})}
            className="form-select"
          >
            <option value="standard">Standard</option>
            <option value="complex">Komplex</option>
            <option value="industrial">Industriell</option>
          </select>
        </div>
        
        <div className="form-checkbox">
          <input
            type="checkbox"
            id="emergency"
            checked={formData.is_emergency}
            onChange={(e) => setFormData({...formData, is_emergency: e.target.checked})}
            className="checkbox-input"
          />
          <label htmlFor="emergency" className="checkbox-label">
            Akut arbete (extra avgift tillkommer)
          </label>
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="submit-button"
        >
          {loading ? 'Beräknar...' : '🧮 Beräkna pris med AI'}
        </button>
      </form>
      
      {result && (
        <div className="calculator-result">
          <div className="result-header">
            <h4 className="result-title">💰 Prisberäkning</h4>
          </div>
          <div className="result-details">
            <div className="detail-item">
              <span className="detail-label">Beräknade timmar:</span>
              <span className="detail-value">{result.hours_estimated}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Arbetskostnad:</span>
              <span className="detail-value">{result.labor_cost} SEK</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Materialkostnad:</span>
              <span className="detail-value">{result.material_cost} SEK</span>
            </div>
            <div className="detail-item total">
              <span className="detail-label">Totalt (ex. moms):</span>
              <span className="detail-value">{result.total_cost} SEK</span>
            </div>
            
            {result.avdrag_type && (
              <div className="avdrag-info">
                <div className="avdrag-header">
                  🎉 {result.avdrag_type} {result.avdrag_percentage}%
                </div>
                <div className="avdrag-savings">
                  <span className="savings-label">Pris efter avdrag:</span>
                  <span className="savings-value">{result.total_with_avdrag} SEK</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="ai-analysis">
            <h5 className="analysis-title">🤖 AI-Analys:</h5>
            <p className="analysis-text">{result.description}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Company Logo Component - NEW DESIGN AS REQUESTED
const CompanyLogo = ({ isDarkMode }) => (
  <div className="company-logo-new-design">
    <div className="logo-section">
      <img 
        src="/current-sd-elektriska-logo.png"
        alt="SD Elektriska AB" 
        className="logo-image-new"
      />
    </div>
    <div className="ai-section">
      <h1 className="ai-text-large">AI</h1>
    </div>
    <div className="text-section">
      <h2 className="certified-text">Certified Electricians</h2>
      <p className="tagline-text">Professional Electrical Solutions in Sweden</p>
    </div>
    <div className="contact-section">
      <div className="liquid-glass-button phone-button">
        <svg className="contact-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <div className="button-content">
          <span className="contact-label">Växel</span>
          <a href="tel:0175100070" className="contact-value">0175 100 70</a>
        </div>
      </div>
      <div className="liquid-glass-button email-button">
        <svg className="contact-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        <div className="button-content">
          <span className="contact-label">Mejl</span>
          <a href="mailto:info@sdelai.se" className="contact-value">info@sdelai.se</a>
        </div>
      </div>
    </div>
  </div>
);

// Main App Component
const App = () => {
  const { isDarkMode } = useThemeManager();
  const [activeTab, setActiveTab] = useState('home');
  const [assistantMode, setAssistantMode] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company-info`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  return (
    <div className={`app-container ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <CompanyLogo isDarkMode={isDarkMode} />
        </div>
      </header>

      {/* Navigation */}
      <nav className="app-navigation">
        <div className="nav-content">
          {[
            { key: 'home', label: 'Hem', icon: 'home' },
            { key: 'calculator', label: 'Prisberäkning', icon: 'calculator' },
            { key: 'assistant', label: 'AI-Assistent', icon: 'assistant' },
            { key: 'services', label: 'Tjänster', icon: 'services' },
            { key: 'contact', label: 'Kontakt', icon: 'contact' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`nav-button ${activeTab === tab.key ? 'active' : ''}`}
            >
              <div className="nav-icon-3d">
                {tab.icon === 'home' && (
                  <svg viewBox="0 0 24 24" className="icon-svg">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                )}
                {tab.icon === 'calculator' && (
                  <svg viewBox="0 0 24 24" className="icon-svg">
                    <rect x="4" y="2" width="16" height="20" rx="2" />
                    <line x1="8" y1="6" x2="16" y2="6" />
                    <line x1="8" y1="10" x2="16" y2="10" />
                    <line x1="8" y1="14" x2="12" y2="14" />
                    <line x1="8" y1="18" x2="12" y2="18" />
                    <rect x="16" y="14" width="0" height="4" />
                  </svg>
                )}
                {tab.icon === 'assistant' && (
                  <svg viewBox="0 0 24 24" className="icon-svg">
                    <rect x="6" y="4" width="12" height="12" rx="2" />
                    <circle cx="9" cy="9" r="1" />
                    <circle cx="15" cy="9" r="1" />
                    <path d="M9 13h6" />
                    <path d="M7 16v2a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2" />
                    <path d="M10 20v2" />
                    <path d="M14 20v2" />
                  </svg>
                )}
                {tab.icon === 'services' && (
                  <svg viewBox="0 0 24 24" className="icon-svg">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                )}
                {tab.icon === 'contact' && (
                  <svg viewBox="0 0 24 24" className="icon-svg">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                )}
              </div>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="app-main">
        {activeTab === 'home' && (
          <div className="home-page">
            {/* Hero Section */}
            <section className="hero-section-professional">
              <div className="hero-content-business">
                <div className="hero-title-business">
                  <h2>PROFESSIONAL ELECTRICAL SERVICES</h2>
                  <h3>AI-POWERED • CERTIFIED • RELIABLE</h3>
                  <p>Advanced electrical solutions for residential, commercial & industrial projects in Sweden</p>
                </div>
                
                <div className="hero-stats">
                  <div className="stat-item">
                    <div className="stat-number">100+</div>
                    <div className="stat-label">COMPLETED PROJECTS</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">24/7</div>
                    <div className="stat-label">AI ASSISTANT SUPPORT</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">AI</div>
                    <div className="stat-label">POWERED TOOLS</div>
                  </div>
                </div>

                <div className="hero-demo-business">
                  <h3>CHOOSE YOUR CONSULTATION METHOD:</h3>
                  <MatrixPillSelector 
                    onSelect={setAssistantMode} 
                    currentMode={assistantMode}
                  />
                </div>

                {assistantMode && (
                  <div className="hero-assistant-business">
                    <AIAssistant mode={assistantMode} isDarkMode={isDarkMode} />
                  </div>
                )}

                <div className="hero-actions-business">
                  <button 
                    onClick={() => setActiveTab('calculator')}
                    className="hero-button-modern-primary"
                  >
                    <div className="hero-button-icon">
                      <svg viewBox="0 0 24 24" className="button-icon-svg">
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <line x1="8" y1="6" x2="16" y2="6" />
                        <line x1="8" y1="10" x2="16" y2="10" />
                        <line x1="8" y1="14" x2="12" y2="14" />
                        <line x1="8" y1="18" x2="12" y2="18" />
                        <circle cx="16" cy="16" r="2" />
                      </svg>
                    </div>
                    <div className="hero-button-content">
                      <h4>Instant Price Calculation</h4>
                      <p>AI-powered pricing with real project data</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('assistant')}
                    className="hero-button-modern-secondary"
                  >
                    <div className="hero-button-icon">
                      <svg viewBox="0 0 24 24" className="button-icon-svg">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        <path d="M8 9h8" />
                        <path d="M8 13h6" />
                      </svg>
                    </div>
                    <div className="hero-button-content">
                      <h4>Professional AI Consultation</h4>
                      <p>Expert electrical advice with AI assistant</p>
                    </div>
                  </button>
                </div>
              </div>
            </section>

            {/* Quick Access */}
            <section className="quick-access">
              <h3>Snabb åtkomst</h3>
              <div className="quick-grid">
                {/* LIQUID GLASS CALCULATOR - ИСПРАВЛЕННЫЙ с 10 кнопками */}
                <div className="quick-item futuristic calculator-block" onClick={() => setActiveTab('calculator')}>
                  <div className="liquid-glass-calculator">
                    <div className="glass-body">
                      <div className="glass-screen">
                        <div className="screen-display">550 × 8 = 4400 SEK</div>
                      </div>
                      <div className="glass-buttons">
                        <div className="button-row">
                          <div className="glass-btn">1</div>
                          <div className="glass-btn">2</div>
                          <div className="glass-btn">3</div>
                        </div>
                        <div className="button-row">
                          <div className="glass-btn">4</div>
                          <div className="glass-btn">5</div>
                          <div className="glass-btn">6</div>
                        </div>
                        <div className="button-row">
                          <div className="glass-btn">7</div>
                          <div className="glass-btn">8</div>
                          <div className="glass-btn">9</div>
                        </div>
                        <div className="button-row">
                          <div className="glass-btn zero">0</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Flying formulas around the glass calculator */}
                    <div className="floating-formulas">
                      <span className="floating-formula formula-1">kW=P/1000</span>
                      <span className="floating-formula formula-2">I=P/(U×√3)</span>
                      <span className="floating-formula formula-3">550 SEK/h</span>
                      <span className="floating-formula formula-4">ROT 30%</span>
                      <span className="floating-formula formula-5">kVA</span>
                      <span className="floating-formula formula-6">cos φ</span>
                    </div>
                  </div>
                  <h4>AI-Kalkylator</h4>
                  <p>Få exakta priser baserat på verkliga projekt</p>
                </div>

                {/* AI ASSISTANT - НОВЫЙ ДИЗАЙН С ОДНОЙ КНОПКОЙ */}
                <div className="quick-item futuristic ai-assistant-block" onClick={() => setActiveTab('assistant')}>
                  <div className="liquid-glass-calculator">
                    <div className="glass-body">
                      <div className="glass-screen-large">
                        {/* Базовый текст экрана - скрывается при hover */}
                        <div className="screen-display ai-screen-base">AI Assistant</div>
                        
                        {/* iMessage переписка при hover */}
                        <div className="imessage-chat">
                          <div className="message-bubble ai-bubble msg-1">
                            <span>Hej! Välkommen till SD Elektriska! 👋</span>
                          </div>
                          <div className="message-bubble ai-bubble msg-2">
                            <span>Hur kan jag hjälpa dig idag?</span>
                          </div>
                          <div className="message-bubble user-bubble msg-3">
                            <span>Hej! Jag behöver hjälp med att installera en Laddbox</span>
                          </div>
                          <div className="message-bubble ai-bubble msg-4">
                            <span>Perfekt! Vilken modell av Laddbox vill du installera?</span>
                          </div>
                          <div className="message-bubble user-bubble msg-5">
                            <span>Jag tänkte på en 22kW Laddbox för hemmet</span>
                          </div>
                          <div className="message-bubble ai-bubble msg-6">
                            <span>Utmärkt val! Kostnaden blir ca 25 000 SEK inkl ROT-avdrag 30%</span>
                          </div>
                          <div className="message-bubble user-bubble msg-7">
                            <span>Låter bra! Kan ni komma nästa vecka?</span>
                          </div>
                          <div className="message-bubble ai-bubble msg-8">
                            <span>Absolut! Ring 0175 100 70 så bokar vi tid 📞</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Только одна кнопка Chat */}
                      <div className="single-button-row">
                        <div className="glass-btn zero">Chat</div>
                      </div>
                    </div>
                    
                    {/* Flying chat elements */}
                    <div className="floating-formulas">
                      <span className="floating-formula formula-1">💬 Frågor</span>
                      <span className="floating-formula formula-2">🤖 AI Svar</span>
                      <span className="floating-formula formula-3">📞 24/7</span>
                      <span className="floating-formula formula-4">🇸🇪 Svenska</span>
                      <span className="floating-formula formula-5">🇬🇧 English</span>
                      <span className="floating-formula formula-6">🇷🇺 Русский</span>
                    </div>
                  </div>
                  <h4>AI-Assistent</h4>
                  <p>24/7 hjälp på svenska, engelska eller ryska</p>
                </div>

                {/* BOKA TID - НОВЫЙ ДИЗАЙН КАК AI ASSISTANT */}
                <div className="quick-item futuristic calendar-block" onClick={() => setActiveTab('contact')}>
                  <div className="liquid-glass-calculator">
                    <div className="glass-body">
                      <div className="glass-screen-large">
                        <div className="screen-display">Boka tid</div>
                        
                        {/* Календарь в статике - правильный календарь */}
                        <div className="static-calendar">
                          <div className="calendar-month">Juli 2025</div>
                          <div className="calendar-mini-grid">
                            {/* Дни недели */}
                            <span className="day-names">M</span>
                            <span className="day-names">T</span>
                            <span className="day-names">O</span>
                            <span className="day-names">T</span>
                            <span className="day-names">F</span>
                            <span className="day-names">L</span>
                            <span className="day-names">S</span>
                            
                            {/* Пустые ячейки до 1 июля (1 июля 2025 - вторник, поэтому 1 пустая ячейка) */}
                            <span></span>
                            
                            {/* Все дни июля 2025 */}
                            <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span>
                            <span>7</span><span>8</span><span>9</span><span>10</span><span>11</span><span>12</span><span className="current-date-circle">13</span>
                            <span>14</span><span>15</span><span>16</span><span>17</span><span>18</span><span>19</span><span>20</span>
                            <span>21</span><span>22</span><span>23</span><span>24</span><span>25</span><span>26</span><span>27</span>
                            <span>28</span><span>29</span><span>30</span><span>31</span>
                          </div>
                        </div>
                        
                        {/* Увеличенная дата при hover */}
                        <div className="enlarged-date">
                          <div className="big-date-number">13</div>
                          <div className="big-date-month">Juli 2025</div>
                          <div className="big-date-day">Söndag</div>
                        </div>
                      </div>
                      
                      {/* Только одна кнопка Boka */}
                      <div className="single-button-row">
                        <div className="glass-btn zero pulse-button">Boka</div>
                      </div>
                    </div>
                    
                    {/* Flying calendar elements */}
                    <div className="floating-formulas">
                      <span className="floating-formula formula-1">📅 Boka</span>
                      <span className="floating-formula formula-2">⏰ Tid</span>
                      <span className="floating-formula formula-3">📞 Ring</span>
                      <span className="floating-formula formula-4">💼 Möte</span>
                      <span className="floating-formula formula-5">🏠 Hembesök</span>
                      <span className="floating-formula formula-6">⚡ Akut</span>
                    </div>
                  </div>
                  <h4>Boka tid</h4>
                  <p>Snabb bokning för alla tjänster</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'calculator' && (
          <div className="calculator-page">
            <div className="page-header">
              <h2>🧮 AI-Prisberäkning</h2>
              <p>Exakta priser baserade på verkliga projekt med automatisk inflationskorrigering</p>
            </div>
            <PriceCalculator isDarkMode={isDarkMode} />
          </div>
        )}

        {activeTab === 'assistant' && (
          <div className="assistant-page">
            <div className="page-header">
              <h2>🤖 AI-Assistent</h2>
              <p>Din personliga guide för alla elektriska frågor</p>
            </div>
            
            {!assistantMode && (
              <div className="assistant-selector">
                <h3>Välj ditt sätt att kommunicera:</h3>
                <MatrixPillSelector 
                  onSelect={setAssistantMode} 
                  currentMode={assistantMode}
                />
              </div>
            )}

            {assistantMode && (
              <div className="assistant-container">
                <button 
                  onClick={() => setAssistantMode(null)}
                  className="mode-switch-btn"
                >
                  🔄 Byt läge
                </button>
                <AIAssistant mode={assistantMode} isDarkMode={isDarkMode} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="services-page">
            <div className="page-header">
              <h2>⚡ Våra Tjänster</h2>
              <p>Professionella elinstallationer med AI-support</p>
            </div>
            
            <div className="services-grid">
              {[
                {
                  title: "Laddboxar för elbilar",
                  icon: "🔌",
                  time: "7-8 timmar",
                  avdrag: "Grön teknik-avdrag 50%",
                  description: "Installation med lastbalanserare"
                },
                {
                  title: "Elcentraler",
                  icon: "⚡",
                  time: "8+ timmar",
                  avdrag: "ROT-avdrag 50%",
                  description: "3-fas med 10-16 automater"
                },
                {
                  title: "Smarta hem (Plejd)",
                  icon: "🏠",
                  time: "9 timmar",
                  avdrag: "ROT-avdrag 50%",
                  description: "Komplett smart belysningssystem"
                },
                {
                  title: "Komplex renovering",
                  icon: "🔨",
                  time: "8-10 dagar",
                  avdrag: "ROT-avdrag 50%",
                  description: "Kök, rum och helomläggning"
                }
              ].map((service, index) => (
                <div key={index} className="service-card">
                  <div className="service-icon">{service.icon}</div>
                  <h4 className="service-title">{service.title}</h4>
                  <div className="service-details">
                    <p className="service-time">⏱️ {service.time}</p>
                    <p className="service-avdrag">🎉 {service.avdrag}</p>
                    <p className="service-description">{service.description}</p>
                  </div>
                  <button 
                    onClick={() => setActiveTab('calculator')}
                    className="service-button"
                  >
                    Beräkna pris
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="contact-page">
            <div className="page-header">
              <h2>📞 Kontakt & Bokning</h2>
              <p>Kontakta oss via AI-assistent eller traditionell form</p>
            </div>
            
            <div className="contact-content">
              <div className="contact-info">
                <CompanyLogo isDarkMode={isDarkMode} />
                <div className="contact-details">
                  <div className="contact-item">
                    <span className="contact-icon">📞</span>
                    <a href="tel:0175100070">0175 100 70</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <a href="mailto:info@sdelai.se">info@sdelai.se</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">📍</span>
                    <span>Ekebyholmshöjden 34a, 762 91 Rimbo</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">🕐</span>
                    <span>07:00-18:00 (Lördag till 17:00)</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">🏠</span>
                    <span>Roslagen, Uppsala och Stockholm</span>
                  </div>
                </div>
              </div>

              <div className="booking-options">
                <h3>Boka tid:</h3>
                <div className="booking-methods">
                  <button 
                    onClick={() => {
                      setActiveTab('assistant');
                      setAssistantMode('text');
                    }}
                    className="booking-method ai"
                  >
                    <span className="method-icon">🤖</span>
                    <div className="method-info">
                      <h4>AI-Assistent</h4>
                      <p>Boka via chat - snabbt och enkelt</p>
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => {
                      const whatsappMessage = "Hej! Jag vill boka tid för elinstallation via sdelai.se";
                      const whatsappUrl = `https://wa.me/46175100070?text=${encodeURIComponent(whatsappMessage)}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="booking-method whatsapp"
                  >
                    <span className="method-icon">💬</span>
                    <div className="method-info">
                      <h4>WhatsApp</h4>
                      <p>Direktkontakt via WhatsApp</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-info">
            <p>© 2025 sdelai.se - AI-driven elektriker</p>
            <p>Powered by SD Elektriska AB</p>
          </div>
          <div className="footer-tech">
            <span className="tech-badge">🤖 AI-Powered</span>
            <span className="tech-badge">⚡ Real-time</span>
            <span className="tech-badge">🇸🇪 Swedish Quality</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;